// Dashboard API client — FE-031.
//
// One typed function per BE-029 endpoint. Query parameters are
// serialised through `buildQueryString` so array filters (e.g.
// ``assignee_filter[]``) come out exactly the way the backend's
// ``request.GET.getlist`` parser expects.
//
// All requests ride the same cookie-session ApiClient as the rest of
// the SPA (`api.get` from src/api/client). The TenantResolutionMiddleware
// resolves the broker entity from the session/default-membership; no
// X-Entity-Id header is needed unless the user explicitly switches
// brokerages later.

import { api } from "@/api/client";

import type {
  CfdBottleneckResponse,
  CfdParams,
  CfdResponse,
  ClientAnalyticsResponse,
  ClientPerformanceResponse,
  DashboardCommonFilters,
  DashboardTableQuery,
  EmployeeAnalyticsResponse,
  EmployeePerformanceResponse,
  GoingColdParams,
  BlockingTasksResponse,
  EmployeeDeliveryResponse,
  GoingColdResponse,
  HealthKpisResponse,
  TaskCompletionTrendResponse,
  TouchpointHealthResponse,
  MostOverdueResponse,
  RenewalPipelineParams,
  RenewalPipelineResponse,
  RiskWorkloadResponse,
} from "./types";

// ── Param serialisation ─────────────────────────────────────────────────

const ARRAY_KEYS_AS_BRACKETS = new Set([
  "assignee_filter",
  "client_filter",
  "category",
  "tag",
  "tag_slug",
  "status",
]);

type Primitive = string | number | boolean;
type ParamValue = Primitive | Primitive[] | undefined | null;

/**
 * Build a `?key=value&...` query string with stable key ordering.
 *
 * - Keys with array values use the BE-029 bracket convention
 *   (`assignee_filter[]=a&assignee_filter[]=b`).
 * - `undefined` / `null` / empty arrays are dropped.
 * - Booleans serialise as `"true"` / `"false"` per spec §7.0.12.
 */
export function buildQueryString(params: Record<string, ParamValue>): string {
  const entries: Array<[string, string]> = [];
  for (const key of Object.keys(params).sort()) {
    const value = params[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      const wireKey = ARRAY_KEYS_AS_BRACKETS.has(key) ? `${key}[]` : key;
      for (const item of value) {
        entries.push([wireKey, String(item)]);
      }
      continue;
    }
    if (typeof value === "boolean") {
      entries.push([key, value ? "true" : "false"]);
      continue;
    }
    entries.push([key, String(value)]);
  }
  if (entries.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.append(k, v);
  return `?${sp.toString()}`;
}

// ── Common-filter mapping (camelCase → snake_case wire keys) ────────────

function commonFiltersToParams(
  f: DashboardCommonFilters | undefined,
): Record<string, ParamValue> {
  if (!f) return {};
  // Status flows through as a repeated ``status[]`` param (the
  // bracket convention ``buildQueryString`` already understands).
  // Empty array → param is dropped, so "no filter selected" still
  // returns the unscoped view.
  return {
    start_date: f.startDate,
    end_date: f.endDate,
    assignee_filter: f.assigneeFilter,
    client_filter: f.clientFilter,
    category: f.category,
    tag: f.tag,
    tag_slug: f.tag_slug,
    status: f.statuses ?? undefined,
  };
}

function tableQueryToParams(
  q: DashboardTableQuery | undefined,
): Record<string, ParamValue> {
  if (!q) return {};
  return {
    ...commonFiltersToParams(q),
    sort_by: q.sortBy,
    sort_dir: q.sortDir,
    limit: q.limit,
    offset: q.offset,
  };
}

// ── Endpoint paths ──────────────────────────────────────────────────────

export const DASHBOARD_PATHS = {
  employeeAnalytics: "/api/v1/dashboard/employee_analytics",
  mostOverdue: "/api/v1/dashboard/most_overdue",
  employeePerformance: "/api/v1/dashboard/employee_performance",
  clientAnalytics: "/api/v1/dashboard/client_analytics",
  renewalPipeline: "/api/v1/dashboard/renewal_pipeline",
  clientPerformance: "/api/v1/dashboard/client_performance",
  goingCold: "/api/v1/dashboard/going_cold",
  touchpointHealth: "/api/v1/dashboard/touchpoint_health",
  healthKpis: "/api/v1/dashboard/health_kpis",
  blockingTasks: "/api/v1/dashboard/blocking_tasks",
  employeeDelivery: "/api/v1/dashboard/employee_delivery",
  taskCompletionTrend: "/api/v1/dashboard/task_completion_trend",
  riskWorkload: "/api/v1/dashboard/risk_workload",
  cfd: "/api/v1/dashboard/cfd",
  cfdBottleneck: "/api/v1/dashboard/cfd/bottleneck",
} as const;

// ── Endpoint functions ──────────────────────────────────────────────────

export function getEmployeeAnalytics(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<EmployeeAnalyticsResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<EmployeeAnalyticsResponse>(
    `${DASHBOARD_PATHS.employeeAnalytics}${qs}`,
    opts,
  );
}

export function getMostOverdue(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<MostOverdueResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<MostOverdueResponse>(
    `${DASHBOARD_PATHS.mostOverdue}${qs}`,
    opts,
  );
}

export function getEmployeePerformance(
  params?: DashboardTableQuery,
  opts?: { signal?: AbortSignal },
): Promise<EmployeePerformanceResponse> {
  const qs = buildQueryString(tableQueryToParams(params));
  return api.get<EmployeePerformanceResponse>(
    `${DASHBOARD_PATHS.employeePerformance}${qs}`,
    opts,
  );
}

export function getClientAnalytics(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<ClientAnalyticsResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<ClientAnalyticsResponse>(
    `${DASHBOARD_PATHS.clientAnalytics}${qs}`,
    opts,
  );
}

export function getRenewalPipeline(
  params?: RenewalPipelineParams,
  opts?: { signal?: AbortSignal },
): Promise<RenewalPipelineResponse> {
  const qs = buildQueryString({
    ...commonFiltersToParams(params),
    months: params?.months,
  });
  return api.get<RenewalPipelineResponse>(
    `${DASHBOARD_PATHS.renewalPipeline}${qs}`,
    opts,
  );
}

export function getClientPerformance(
  params?: DashboardTableQuery,
  opts?: { signal?: AbortSignal },
): Promise<ClientPerformanceResponse> {
  const qs = buildQueryString(tableQueryToParams(params));
  return api.get<ClientPerformanceResponse>(
    `${DASHBOARD_PATHS.clientPerformance}${qs}`,
    opts,
  );
}

export function getGoingCold(
  params?: GoingColdParams,
  opts?: { signal?: AbortSignal },
): Promise<GoingColdResponse> {
  const qs = buildQueryString({
    ...commonFiltersToParams(params),
    days: params?.days,
  });
  return api.get<GoingColdResponse>(
    `${DASHBOARD_PATHS.goingCold}${qs}`,
    opts,
  );
}


export function getTouchpointHealth(
  params?: { limit?: number },
  opts?: { signal?: AbortSignal },
): Promise<TouchpointHealthResponse> {
  const qs = buildQueryString({ limit: params?.limit });
  return api.get<TouchpointHealthResponse>(
    `${DASHBOARD_PATHS.touchpointHealth}${qs}`,
    opts,
  );
}


export function getHealthKpis(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<HealthKpisResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<HealthKpisResponse>(
    `${DASHBOARD_PATHS.healthKpis}${qs}`,
    opts,
  );
}


export function getBlockingTasks(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<BlockingTasksResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<BlockingTasksResponse>(
    `${DASHBOARD_PATHS.blockingTasks}${qs}`,
    opts,
  );
}


export function getEmployeeDelivery(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<EmployeeDeliveryResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<EmployeeDeliveryResponse>(
    `${DASHBOARD_PATHS.employeeDelivery}${qs}`,
    opts,
  );
}


export function getTaskCompletionTrend(
  params?: { months?: number },
  opts?: { signal?: AbortSignal },
): Promise<TaskCompletionTrendResponse> {
  const qs = buildQueryString({ months: params?.months });
  return api.get<TaskCompletionTrendResponse>(
    `${DASHBOARD_PATHS.taskCompletionTrend}${qs}`,
    opts,
  );
}

export function getRiskWorkload(
  params?: DashboardCommonFilters,
  opts?: { signal?: AbortSignal },
): Promise<RiskWorkloadResponse> {
  const qs = buildQueryString(commonFiltersToParams(params));
  return api.get<RiskWorkloadResponse>(
    `${DASHBOARD_PATHS.riskWorkload}${qs}`,
    opts,
  );
}

export function getCfd(
  params?: CfdParams,
  opts?: { signal?: AbortSignal },
): Promise<CfdResponse> {
  const qs = buildQueryString({
    range: params?.range,
    category: params?.category,
    assignee: params?.assignee,
    blocked: params?.blocked,
  });
  return api.get<CfdResponse>(`${DASHBOARD_PATHS.cfd}${qs}`, opts);
}

export function getCfdBottleneck(
  opts?: { signal?: AbortSignal },
): Promise<CfdBottleneckResponse> {
  return api.get<CfdBottleneckResponse>(
    DASHBOARD_PATHS.cfdBottleneck,
    opts,
  );
}
